import React, { useContext, useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../css/cart.scss";
import Header from "./common/Header";
import Footer from "./common/Footer";
import { CartContext } from "./common/CartContext";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../apis/SendMessageAPI";
import { BASE_URL } from "../constans/ApiEndpointConstants";
import LocalStorageUtil from "../utils/localStorageUtil";
import { convertEurToGbp, isGbpLanguage } from "../constans/CurrencyConstants";

const Cart = () => {
  const { t, i18n } = useTranslation();
  const formatEuro = (n) => {
    const isUk = isGbpLanguage(i18n.language);
    const locale = isUk ? "en-GB" : "nl-NL";
    const currency = "EUR";
    const amount = n;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace(/\u00A0/g, " ");
  };

  const { items = [], updateItemQty, removeProduct } = useContext(CartContext) || {};
  const getLineKey = (it) => it?.lineKey ?? (it?.skuId != null ? `${it?.id}_${it.skuId}` : String(it?.id));

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) => sum + (Number(it.price) || 0) * (Number(it.qty) || 0),
        0
      ),
    [items]
  );
  /** 购物车页暂不计入运费，与结账页区分；合计 = 小计 */
  const total = subtotal;

  const canUpdateQty = typeof updateItemQty === "function";
  const canRemove = typeof removeProduct === "function";

  // 进入 Cart 页面上报一次
  const [pagePinged, setPagePinged] = useState(false);
  useEffect(() => {
    if (pagePinged) return;
    const gen = () => {
      const ch = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let r = "";
      for (let i = 0; i < 6; i++) r += ch[Math.floor(Math.random() * ch.length)];
      return r;
    };
    let userid = localStorage.getItem("userid");
    if (!userid) {
      userid = gen();
      localStorage.setItem("userid", userid);
    }
    const ad = LocalStorageUtil.getAd?.() ?? "PurityBase";
    const ids =
      Array.isArray(items) && items.length
        ? items.map((it) => it.id).filter(Boolean).join(",")
        : "";
    sendMessage(`${BASE_URL} [Cart Page]:ad=${ad}:userid=${userid}:productids=${ids}`);
    setPagePinged(true);
  }, [pagePinged, items]);

  return (
    <div>
      <Header />
      <main className="cart-page">
        <div className="cart__container">
          <nav className="cart-page__crumb" aria-label={t("terms.breadcrumbAria")}>
            <Link to="/">{t("pd.crumbs.home")}</Link>
          </nav>
          <h1 className="cart__title">{t("cart.title")}</h1>
          <div className="cart__layout">
            <section className="cart-card cart__layout_first" aria-label={t("cart.aria.items")}>
              {items.length === 0 ? (
                <div className="cart-empty">{t("cart.empty")}</div>
              ) : (
                <>
                  <div className="cart-table cart-row cart-row--head">
                    <div className="col col-product">{t("cart.table.product")}</div>
                    <div className="col col-qty">{t("cart.table.qty")}</div>
                    <div className="col col-sub">{t("cart.table.subtotal")}</div>
                    <div className="col col-remove" aria-hidden="true" />
                  </div>
                  {items.map((it) => {
                    const lineKey = getLineKey(it);
                    const q = Number(it.qty) || 0;
                    const unit = Number(it.price) || 0;
                    const compareRaw =
                      Number(it.compareAtPrice) || Number(it.originalPrice) || 0;
                    const showDiscount =
                      compareRaw > 0 && compareRaw > unit + 0.001;
                    const lineCurrent = unit * q;
                    const lineOrig = compareRaw * q;

                    const bumpQty = (delta) => {
                      if (!canUpdateQty || !lineKey) return;
                      updateItemQty(lineKey, Math.max(1, q + delta));
                    };

                    return (
                      <div className="cart-row" key={lineKey}>
                        <div
                          className="col col-product"
                          data-label={t("cart.table.product") + ":"}
                        >
                          <div className="cart-prod">
                            <img
                              className="cart-thumb"
                              src={it.thumb || "/main/main1.avif"}
                              alt={it.title}
                            />
                            <div className="cart-info">
                              <div className="cart-title">{it.title}</div>
                              {Array.isArray(it.options) &&
                                it.options.map((op, idx) => (
                                  <div className="cart-opt" key={idx}>
                                    {op}
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                        <div
                          className="col col-qty"
                          data-label={t("cart.table.qty") + ":"}
                        >
                          <div className="cart-page__qtyStepper">
                            <button
                              type="button"
                              className="cart-page__qtyBtn"
                              aria-label={t("drawer.qtyDecrease")}
                              disabled={!canUpdateQty || !lineKey || q <= 1}
                              onClick={() => bumpQty(-1)}
                            >
                              −
                            </button>
                            <span className="cart-page__qtyVal">{q}</span>
                            <button
                              type="button"
                              className="cart-page__qtyBtn"
                              aria-label={t("drawer.qtyIncrease")}
                              disabled={!canUpdateQty || !lineKey}
                              onClick={() => bumpQty(1)}
                            >
                              +
                            </button>
                          </div>
                        </div>
                        <div
                          className="col col-sub"
                          data-label={t("cart.table.subtotal") + ":"}
                        >
                          {showDiscount ? (
                            <span className="cart-line__prices">
                              <del className="cart-line__strike">
                                {formatEuro(lineOrig)}
                              </del>
                              <span className="cart-line__sale">
                                {formatEuro(lineCurrent)}
                              </span>
                            </span>
                          ) : (
                            <span className="cart-line__plain">{formatEuro(lineCurrent)}</span>
                          )}
                        </div>
                        <div className="col col-remove">
                          <button
                            type="button"
                            className="cart-remove cart-remove--text"
                            aria-label={t("cart.aria.remove")}
                            onClick={() => canRemove && lineKey && removeProduct(lineKey)}
                          >
                            {t("cart.aria.remove")}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </section>

            <aside className="summary-card" aria-label={t("cart.summary.total")}>
              <h2 className="summary__title">{t("cart.summary.title")}</h2>
              <div className="summary__row">
                <span>{t("cart.summary.subtotal")}</span>
                <strong>{formatEuro(subtotal)}</strong>
              </div>

              <div className="summary__divider" aria-hidden="true" />

              <div className="summary__row summary__total">
                <span>{t("cart.summary.total")}</span>
                <strong className="summary__totalAmount">{formatEuro(total)}</strong>
              </div>
              <a href="/checkout" className="checkout-btn">
                {t("cart.checkoutBtn")}
              </a>
              <Link to="/shop" className="cart-page__continue">
                {t("cart.continueShopping")}
              </Link>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Cart;
